import { Component, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { SocialMediaService } from '../social-media.service';
import { SwalUtils } from '../../../../shared/utils/swal.utils';
import { PermissionService } from '../../../../core/service/permission/permission.service';

export interface SocialMedia {
  social_media_id: number;
  social_media_platform: string;
  social_media_url: string;
  social_media_order: number;
  social_media_status: 'ACTI' | 'INAC';
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

@Component({
  selector: 'app-social-media-list',
  standalone: false,
  templateUrl: './social-media-list.component.html',
  styleUrls: ['./social-media-list.component.scss']
})
export class SocialMediaListComponent implements OnInit, OnDestroy {
  items = signal<SocialMedia[]>([]);
  isLoading = signal(true);
  isFiltering = signal(false);
  errorMessage = signal<string | null>(null);
  searchQuery = signal('');
  selectedStatus = signal('');
  pagination = signal<Pagination>({ page: 1, limit: 10, total: 0, totalPages: 0 });

  pendingStatus = signal<{ item: SocialMedia; newStatus: 'ACTI' | 'INAC' } | null>(null);
  pendingDelete = signal<SocialMedia | null>(null);

  pageNumbers = computed((): (number | '...')[] => {
    const { page, totalPages } = this.pagination();
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages: (number | '...')[] = [1];
    if (page > 3) pages.push('...');
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) pages.push(i);
    if (page < totalPages - 2) pages.push('...');
    pages.push(totalPages);
    return pages;
  });

  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private socialMediaService: SocialMediaService,
    private router: Router,
    public permService: PermissionService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(300), takeUntil(this.destroy$)).subscribe(query => {
      this.searchQuery.set(query);
      this.loadItems(1, true);
    });
    this.loadItems(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSubject.complete();
  }

  private loadItems(page: number, filtering = false): void {
    filtering ? this.isFiltering.set(true) : this.isLoading.set(true);
    this.errorMessage.set(null);

    this.socialMediaService.getSocialMedia(page, this.pagination().limit, this.searchQuery(), this.selectedStatus())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.items.set(response.data || []);
          const total = response.pagination?.total || response.total || 0;
          this.pagination.set({
            page,
            limit: this.pagination().limit,
            total,
            totalPages: Math.ceil(total / this.pagination().limit),
          });
          this.isLoading.set(false);
          this.isFiltering.set(false);
        },
        error: (err) => {
          this.errorMessage.set(err?.error?.message || 'Erreur lors du chargement des réseaux sociaux');
          this.isLoading.set(false);
          this.isFiltering.set(false);
        }
      });
  }

  onSearch(value: string): void { this.searchSubject.next(value); }

  onStatusFilter(value: string): void {
    this.selectedStatus.set(value);
    this.loadItems(1, true);
  }

  onPageChange(page: number): void {
    const { totalPages } = this.pagination();
    if (page < 1 || page > totalPages) return;
    this.loadItems(page);
  }

  onAddNew(): void { this.router.navigate(['/federation/reseaux-sociaux/new']); }
  onEdit(id: number): void { this.router.navigate(['/federation/reseaux-sociaux', id, 'edit']); }

  onToggleStatus(item: SocialMedia, event: Event): void {
    event.stopPropagation();
    const newStatus = (item.social_media_status === 'ACTI') ? 'INAC' : 'ACTI';
    this.pendingStatus.set({ item, newStatus });
  }

  cancelStatus(): void { this.pendingStatus.set(null); }

  applyStatus(): void {
    const pending = this.pendingStatus();
    if (!pending) return;
    this.pendingStatus.set(null);
    this.socialMediaService.setStatus(pending.item.social_media_id, pending.newStatus)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.loadItems(this.pagination().page),
        error: (err) => SwalUtils.error('Erreur', err?.error?.message || 'Erreur lors du changement de statut')
      });
  }

  onDelete(item: SocialMedia, event: Event): void {
    event.stopPropagation();
    this.pendingDelete.set(item);
  }

  cancelDelete(): void { this.pendingDelete.set(null); }

  applyDelete(): void {
    const item = this.pendingDelete();
    if (!item) return;
    this.pendingDelete.set(null);
    this.socialMediaService.deleteSocialMedia(item.social_media_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          SwalUtils.success('Supprimé', 'Le réseau social a été supprimé.');
          this.loadItems(this.pagination().page);
        },
        error: (err) => SwalUtils.error('Erreur', err?.error?.message || 'Erreur lors de la suppression')
      });
  }

  statusLabel(status: string | null): string {
    if (status === 'ACTI') return 'En ligne';
    return 'Hors ligne';
  }

  statusBadgeClass(status: string | null): string {
    if (status === 'ACTI') return 'bg-emerald-50 text-emerald-600 border-emerald-100';
    return 'bg-amber-50 text-amber-600 border-amber-100';
  }

  platformIcon(platform: string): string {
    const lower = (platform || '').toLowerCase();
    if (lower.includes('facebook')) return 'fb';
    if (lower.includes('instagram')) return 'ig';
    if (lower.includes('twitter') || lower.includes('x')) return 'tw';
    if (lower.includes('youtube')) return 'yt';
    if (lower.includes('tiktok')) return 'tk';
    if (lower.includes('linkedin')) return 'li';
    return 'web';
  }
}
